<template>
  <!-- Component containing dropdowns for "exploring" content -->
  <!-- One dropdown per level -->
  <div id="side-navigation-container">
    <div id="side-navigation">
      <div id="side-navigation-title" @click="dropdownsClickHandler">
        <div>
          Make Your Selection
          <font-awesome-icon
            :icon="['fas', 'chevron-down']"
            class="side-navigation-arrow"
            v-if="lessThan1199 && !showAllDropdowns"
          />
          <font-awesome-icon
            :icon="['fas', 'chevron-up']"
            class="side-navigation-arrow"
            v-else-if="lessThan1199 && showAllDropdowns"
          />
        </div>
      </div>
      <div id="side-navigation-dropdowns" v-show="showAllDropdowns">
        <div class="side-navigation-select">
          <!-- Show Theme dropdown only if exploring by Theme -->
          <div v-if="theme">
            <!-- Numbering for labels changes depending on fields available (see below) -->
            <label class="select-label" for="theme-select">1. Themes</label>
            <!-- On selection, load new information -->
            <select
              id="theme-select"
              class="select"
              ref="themeSelect"
              v-model="themeId"
              @change="goTo($event, 'theme', themeId)"
            >
              <option selected disabled value="">Select a Theme</option>
              <option
                v-for="theme in themes"
                :key="theme.themeId"
                :value="theme.themeId"
              >
                {{ theme.themeName }}
              </option>
            </select>
          </div>
        </div>
        <div class="side-navigation-select">
          <div>
            <label class="select-label" for="country-select">
              <span v-if="!theme">1. </span>
              <span v-else>2. </span>
              Countries
            </label>
            <select
              id="country-select"
              class="select"
              ref="countrySelect"
              v-model="countryId"
              @change="goTo($event, 'country', countryId)"
            >
              <option selected disabled value="">Select a Country</option>
              <option
                v-for="country in countries"
                :key="country.countryId"
                :value="country.countryId"
              >
                {{ country.countryName }}
              </option>
            </select>
          </div>
        </div>

        <!-- Filters dropdown, only show on Country path -->
        <div v-if="!theme && !isItineraryPath && filtersExist" class="side-navigation-select">
          <!-- White overlay to indicate "disabled", applied when necessary -->
          <div
            v-if="filterDisabled"
            class="disabled-overlay"
            :class="{ disabled: filterDisabled }"
          ></div>
          <div>
            <label class="select-label" for="filters-select">
              <span v-if="!theme">2. </span>
              <span v-else>3. </span>
              Filters
            </label>
            <!-- Select field is disabled if current "level" is two or more "levels" above -->
            <select
              id="filter-select"
              class="select"
              ref="filterSelect"
              v-model="filterId"
              @change="goTo($event, 'filter', filterId)"
              :disabled="filterDisabled"
            >
              <option selected disabled value="">Select a Filter</option>
              <option
                v-for="filter in filters"
                :key="filter.filterId"
                :value="filter.filterId"
              >
                {{ filter.filterName }}
              </option>
            </select>
            <router-link 
              id="filter-reset"
              v-if="filterId !== ''"
              :to="{ name: 'countries', params: { country: `c-${countryId}` } }">
              Reset Filter
            </router-link>
          </div>

        </div>

        <div v-if="territoriesExist" class="side-navigation-select">
          <!-- White overlay to indicate "disabled", applied when necessary -->
          <div
            v-if="territoryDisabled"
            class="disabled-overlay"
            :class="{ disabled: territoryDisabled }"
          ></div>
          <div>
            <label class="select-label" for="territory-select">
              <span v-if="!theme && (!filtersExist || isItineraryPath)">2. </span>
              <span v-else>3. </span>
              Territories
            </label>
            <!-- Select field is disabled if current "level" is two or more "levels" above -->
            <select
              id="territory-select"
              class="select"
              ref="territorySelect"
              v-model="territoryId"
              @change="goTo($event, 'territory', territoryId)"
              :disabled="territoryDisabled"
            >
              <option selected disabled value="">Select a Territory</option>
              <option
                v-for="territory in territories"
                :key="territory.territoryId"
                :value="territory.territoryId"
              >
                {{ territory.territoryName }}
              </option>
            </select>
          </div>
        </div>

        <div class="side-navigation-select" v-if="isItineraryPath">
          <div>
            <label class="select-label" for="itinerary-select">
              <span v-if="!territoriesExist">2. </span>
              <span v-else> 3.</span>
              Itineraries
            </label>
            <select
              id="itinerary-select"
              class="select"
              ref="itinerarySelect"
              v-model="itineraryId"
              @change="goTo($event, 'itinerary', itineraryId)"
            >
              <option selected disabled value="">Select an Itinerary</option>
              <option
                v-for="itinerary in itineraries"
                :key="itinerary.itineraryId"
                :value="itinerary.itineraryId"
              >
                {{ itinerary.itineraryName }}
              </option>
            </select>
          </div>
        </div>
        <div class="side-navigation-select" v-if="isItineraryPath">
          <div
            v-if="subItineraryDisabled"
            class="disabled-overlay"
            :class="{ disabled: subItineraryDisabled }"
          ></div>
          <div>
            <label class="select-label" for="subItinerary-select">
              <span v-if="!territoriesExist">3. </span>
              <span v-else>4. </span>
              Sub-Itineraries
            </label>
            <select
              id="subItinerary-select"
              class="select"
              ref="subItinerarySelect"
              v-model="subItineraryId"
              @change="goTo($event, 'subItinerary', subItineraryId)"
              :disabled="subItineraryDisabled"
            >
              <option selected disabled value="">Select a Sub-Itinerary</option>
              <option
                v-for="subItinerary in subItineraries"
                :key="subItinerary.subItineraryId"
                :value="subItinerary.subItineraryId"
              >
                {{ subItinerary.subItineraryName }}
              </option>
            </select>
          </div>
        </div>

        <!-- <div class="side-navigation-select" v-if="!isItineraryPath"> -->
        <div class="side-navigation-select">
          <div
            v-if="locationDisabled"
            class="disabled-overlay"
            :class="{ disabled: locationDisabled }"
          ></div>
          <div>
            <label class="select-label" for="location-select">
              <span
                v-if="
                  (this.$route.name === 'themes' && territoriesExist) ||
                  (this.$route.name === 'itineraries' && !territoriesExist) ||
                  (this.$route.name === 'countries' && filtersExist && territoriesExist)
                "
                >4.
              </span>
              <span
                v-else-if="
                  this.$route.name === 'itineraries' && territoriesExist
                "
                >5.
              </span>
              <span v-else-if="!theme && !territoriesExist && !filtersExist">2. </span>
              <span
                v-else-if="(!theme && territoriesExist) || !territoriesExist"
                >3.
              </span>
              Locations
            </label>
            <select
              id="location-select"
              class="select"
              ref="locationSelect"
              v-model="locationId"
              @change="goTo($event, 'location', locationId)"
              :disabled="locationDisabled"
            >
              <option selected disabled value="">Select a Location</option>
              <option
                v-for="location in locations"
                :key="location.locationId"
                :value="location.locationId"
              >
                {{ location.locationName }}
              </option>
            </select>
          </div>
        </div>
        <!-- <div class="side-navigation-select" v-if="!isItineraryPath"> -->
        <div class="side-navigation-select">
          <div
            v-if="monumentDisabled"
            class="disabled-overlay"
            :class="{ disabled: monumentDisabled }"
          ></div>
          <div>
            <label class="select-label" for="monument-select">
              <span
                v-if="
                  (this.$route.name === 'themes' && territoriesExist) ||
                  (this.$route.name === 'itineraries' && !territoriesExist) ||
                  (this.$route.name === 'countries' && filtersExist && territoriesExist)
                "
                >5.
              </span>
              <span
                v-else-if="
                  this.$route.name === 'itineraries' && territoriesExist
                "
                >6.
              </span>
              <span v-else-if="!theme && !territoriesExist && !filtersExist">3. </span>
              <span
                v-else-if="(!theme && territoriesExist) || !territoriesExist"
                >4.
              </span>
              Monuments
            </label>
            <select
              id="monument-select"
              class="select"
              ref="monumentSelect"
              v-model="monumentId"
              @change="goTo($event, 'monument', monumentId)"
              :disabled="monumentDisabled"
            >
              <option selected disabled value="">Select a Monument</option>
              <option
                v-for="monument in monuments"
                :key="monument.monumentId"
                :value="monument.monumentId"
              >
                {{ monument.monumentName }}
              </option>
            </select>
          </div>
        </div>

        <!-- Link to go back to homepage for users who want to switch exploration method -->
        <div class="side-navigation-select">
          <div>
            <p>
              You are currently exploring by
              <span class="bold" v-if="isItineraryPath">Itinerary.</span>
              <span class="bold" v-else-if="theme">Theme.</span>
              <span class="bold" v-else>Country.</span>
              To explore by
              <span v-if="isItineraryPath"
                >Theme or Country, or to see a full list of Itineraries,
              </span>
              <span v-else-if="!theme">Theme or Itinerary, </span>
              <span v-else>Country or Itinerary, </span>
              <router-link id="side-navigation-home" :to="{ name: 'home' }"
                >return to the
                <span class="bold">homepage.</span>
              </router-link>
            </p>
          </div>
        </div>

        <!-- Display partnership logos -->
        <div v-if="allPartners.length > 0" class="partners">
          <div
            class="partner"
            v-for="partner in allPartners"
            :key="partner.title"
          >
            <a
              class="partner-link"
              v-if="partner.url.length > 0"
              :href="partner.url"
              target="_blank"
            >
              <div class="partner-logo-container">
                <img class="partner-logo" :src="partner.logo" />
              </div>
            </a>
            <span class="partner-title">
              {{ partner.title }}
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import language from "../mixins/language.js";
import media from "../mixins/media.js";
export default {
  name: "SideNavigation",
  mixins: [language, media],
  props: [
    "theme",
    "country",
    "filter",
    "territory",
    "itinerary",
    "subItinerary",
    "subItineraryLocationIndex",
    "subItineraryLocation",
    "subItineraryLocationId",
    "location",
    "monument",
  ],
  data() {
    return {
      isItineraryPath: false,

      filterDisabled: false,
      territoryDisabled: false,
      subItineraryDisabled: false,
      locationDisabled: false,
      monumentDisabled: false,

      filtersExist: true,
      territoriesExist: true,

      themes: [],
      themeId: this.strip(this.theme),
      themeName: "",

      countries: [],
      countryId: this.strip(this.country),
      countryName: "",

      filters: [],
      filterId: this.strip(this.filter),
      filterName: "",

      territories: [],
      territoryId: this.strip(this.territory),
      territoryName: "",

      itineraries: [],
      itineraryId: this.strip(this.itinerary),
      itineraryName: "",

      subItineraries: [],
      subItineraryId: this.strip(this.subItinerary),
      subItineraryName: "",

      locations: [],
      locationId: this.strip(this.location),
      locationName: "",

      monuments: [],
      monumentId: this.strip(this.monument),
      monumentName: "",

      locationInfo: [],

      partners: {},
      allPartners: [],

      lessThan1199: window.matchMedia("(max-width: 1199px)").matches,
      // Using lessThan1199 on page load doesn't work, have to check manually
      showAllDropdowns: window.matchMedia("(max-width: 1199px)").matches
        ? false
        : true,
    };
  },
  watch: {
    $route(to, from) {
      this.checkPath(this.$route.name);
      this.reset();
      if (this.theme) {
        this.getList(this.theme, "themes", "themeId", "themeName");
      }
      this.getList(this.country, "countries", "countryId", "countryName");
      this.getList(this.filter, "filters", "filterId", "filterName");
      this.getList(
        this.territory,
        "territories",
        "territoryId",
        "territoryName"
      );
      if (this.$route.name === "itineraries") {
        this.getList(
          this.itinerary,
          "itineraries",
          "itineraryId",
          "itineraryName"
        );
        this.getList(
          this.subItinerary,
          "subItineraries",
          "subItineraryId",
          "subItineraryName"
        );
      }
      if (this.$route.name !== "itineraries") {
        // Only execute if Country has been selected
        if (this.country) {
          this.getList(
            this.location,
            "locations",
            "locationId",
            "locationName"
          );
          this.getList(
            this.location,
            "monuments",
            "monumentId",
            "monumentName"
          );
        }
      }
      this.setDisabled();
    },
    subItineraryLocationIndex() {
      this.locationId =
        this.locations[this.subItineraryLocationIndex].locationId;
      let locationMonuments =
        this.locationInfo[this.subItineraryLocationIndex].monuments;
      let newMonuments = [];
      for (let i = 0; i < locationMonuments.length; i++) {
        let m = {};
        m.monumentId = locationMonuments[i].monumentId;
        m.monumentName = this.returnValueInLanguage(
          "en",
          locationMonuments[i].monumentName
        ).value;
        newMonuments.push(m);
      }
      this.monuments = [];
      this.monuments = newMonuments;
    },
    subItineraryLocation() {
      this.locationId = this.subItineraryLocation;
    },
    subItineraryLocationId() {
      this.locationId = this.subItineraryLocationId;
    },
    lessThan1199() {
      if (!this.lessThan1199) {
        this.showAllDropdowns = true;
      } else {
        this.showAllDropdowns = false;
      }
    },
  },
  methods: {
    dropdownsClickHandler: function () {
      if (!this.lessThan1199) {
        return;
      } else {
        this.showAllDropdowns = !this.showAllDropdowns;
      }
    },
    nameSort: function (arr, key) {
      // Include collator object to handle names with accented characters
      const collator = new Intl.Collator("en", {
        numeric: true,
        sensitivity: "base",
      });
      arr.sort((a, b) => {
        let nameA = a[key];
        let nameB = b[key];
        return collator.compare(nameA, nameB);
      });
    },
    checkPath: function (route) {
      if (route === "itineraries") {
        this.isItineraryPath = true;
      } else {
        this.isItineraryPath = false;
      }
    },
    // Set which dropdowns should be disabled
    // CSS disabled overlay rules also depend on these being set
    setDisabled: function () {
      this.filterDisabled = false;
      this.territoryDisabled = false;
      this.subItineraryDisabled = false;
      this.locationDisabled = false;
      this.monumentDisabled = false;
      // If exploring by Theme but Country is not yet selected, disable other fields
      if (this.theme && !this.country) {
        this.territoryDisabled = true;
        this.locationDisabled = true;
      }
      // Covers scenario where Territory is selected but not a Location yet
      // Only apply to non-Itinerary paths
      if (!this.location) {
        // Monument should be enabled if Sub-Itinerary is selected
        // due to Location being automatically selected
        if (!this.subItinerary) {
          this.monumentDisabled = true;
        }
      }
      // If no Itinerary has been selected, Sub-Itinerary should be disabled
      // Disable Location as well
      if (!this.itinerary && this.$route.name === "itineraries") {
        this.subItineraryDisabled = true;
        this.locationDisabled = true;
      }
      // If no Sub-Itinerary has been selected, Location should be disabled
      if (!this.subItinerary && this.$route.name === "itineraries") {
        this.locationDisabled = true;
      }
    },
    // Reset data according to URL
    reset: function () {
      this.filtersExist = true;
      this.territoriesExist = true;
      this.themes = [];
      this.themeId = this.strip(this.theme);
      this.themeName = "";
      this.countries = [];
      this.countryId = this.strip(this.country);
      this.countryName = "";
      this.filters = [];
      this.filterId = this.strip(this.filter);
      this.filterName = "";
      this.territories = [];
      this.territoryId = this.strip(this.territory);
      this.territoryName = "";
      this.itineraries = [];
      this.itineraryId = this.strip(this.itinerary);
      this.itineraryName = "";
      this.subItineraries = [];
      this.subItineraryId = this.strip(this.subItinerary);
      this.subItineraryName = "";
      this.locations = [];
      this.locationId = this.strip(this.location);
      this.locationName = "";
      this.monuments = [];
      this.monumentId = this.strip(this.monument);
      this.monumentName = "";

      this.locationInfo = [];

      this.partners = {};
      this.allPartners = [];
    },
    // On dropdown select, update data for new page
    goTo: function (e, category, id) {
      // On Sub-Itinerary pages, Locations do not route to new page
      if (this.subItinerary && category === "location") {
        if (!this.monument) {
          // Index of selected
          let index = e.target.selectedOptions[0].index - 1;
          let locationMonuments = this.locationInfo[index].monuments;
          let newMonuments = [];
          // Get new list of Monuments for Location
          for (let i = 0; i < locationMonuments.length; i++) {
            let m = {};
            m.monumentId = locationMonuments[i].monumentId;
            m.monumentName = this.returnValueInLanguage(
              "en",
              locationMonuments[i].monumentName
            ).value;
            newMonuments.push(m);
          }
          this.monuments = [];
          this.monuments = newMonuments;
          this.$emit("change-location", index);
          return;
        } else {
          // Create URL for navigation, which determines data to get/load on Explore page
          let params = this.$route.path.substring(1).split("/");
          params.pop();
          let name = params[0];
          let country = params[1];
          // let territory, filter, itinerary, subItinerary;
          let filter, territory, itinerary, subItinerary;
          if (params[2].includes("f-")) {
            filter = params[2];
            if (params[3].includes("tr-")) {
              territory = params[3];
              itinerary = params[4];
              subItinerary = params[5];
            } else {
              itinerary = params[3];
              subItinerary = params[4];
            }
          } else if (params[2].includes("tr-")) {
            filter = null;
            territory = params[2];
            itinerary = params[3];
            subItinerary = params[4];
          } else {
            filter = null;
            territory = null;
            itinerary = params[2];
            subItinerary = params[3];
          }
          // if (params[2].includes("te-")) {
          //   territory = params[2];
          //   if (params[3].includes("f-")) {
          //     filter = params[3];
          //     itinerary = params[4];
          //     subItinerary = params[5];
          //   } else {
          //     itinerary = params[3];
          //     subItinerary = params[4];
          //   }
          // } else {
          //   territory = null;
          //   if (params[2].includes("f-")) {
          //     filter = params[2];
          //     itinerary = params[3];
          //     subItinerary = params[4];
          //   } else {
          //     itinerary = params[2];
          //     subItinerary = params[3];
          //   }
          // }
          let obj = {
            name: name,
            params: {
              country: country,
              filter: filter,
              territory: territory,
              itinerary: itinerary,
              subItinerary: subItinerary,
            },
          };
          this.$emit("save-location", e.target.selectedOptions[0].value);
          let index = e.target.selectedOptions[0].index - 1;
          this.$emit("change-location", index);
          return this.$router.push(obj);
        }
      }

      // Navigating in Itinerary path needs to handle Monument selection differently
      if (this.subItinerary && category === "monument") {
        let params = this.$route.path.substring(1).split("/");
        let monument = `m-${e.target.selectedOptions[0].value}`;
        let name = params[0];
        let country = params[1];
        let territory, itinerary, subItinerary;
        if (params[2].includes("tr-")) {
          territory = params[2];
          itinerary = params[3];
          subItinerary = params[4];
        } else {
          territory = null;
          itinerary = params[2];
          subItinerary = params[3];
        }
        let obj = {
          name: name,
          params: {
            country: country,
            territory: territory,
            itinerary: itinerary,
            subItinerary: subItinerary,
            monument: monument,
          },
        };
        return this.$router.push(obj);
      }

      // Emit back to parent component (text names required for breadcrumbs)
      let emitName;
      // Check if IE11
      if (window.document.documentMode) {
        emitName = e.target.options[e.target.selectedIndex].text;
      } else {
        emitName = e.target.selectedOptions[0].text;
      }
      let emit = {
        category: category,
        name: emitName,
      };
      this.$emit("go-to", emit);

      // Create object for giving to $router.push
      let obj = {
        name: "",
        params: {},
      };
      // Determine current route (Themes, Countries, or Itineraries)
      if (this.$route.name === "itineraries") {
        obj.name = "itineraries";
      } else if (this.theme) {
        obj.name = "themes";
      } else {
        obj.name = "countries";
      }
      // Populate appropriate params, adding text markers ("t-", "c-", etc.)
      if (category === "theme") {
        obj.params.theme = `t-${id}`;
      } else {
        if (this.theme) {
          obj.params.theme = this.theme;
        }
        if (
          this.country &&
          (category === "filter" ||
            category === "territory" ||
            category === "itinerary" ||
            category === "subItinerary" ||
            category === "location" ||
            category === "monument")
        ) {
          obj.params.country = this.country;
        }
        if (
          this.filter &&
          (category === "territory" ||
            category === "itinerary" ||
            category === "subItinerary" ||
            category === "location" ||
            category === "monument")
        ) {
          obj.params.filter = this.filter;
        }
        if (
          this.territory &&
          (category === "itinerary" ||
            category === "subItinerary" ||
            category === "location" ||
            category === "monument")
        ) {
          obj.params.territory = this.territory;
        }
        if (this.itinerary && category === "subItinerary") {
          obj.params.itinerary = this.itinerary;
        }
        if (this.location && category === "monument") {
          obj.params.location = this.location;
        }
        // Create depending on selection
        if (category === "country") {
          obj.params.country = `c-${id}`;
        }
        if (category === "filter") {
          obj.params.filter = `f-${id}`;
        }
        if (category === "territory") {
          obj.params.territory = `tr-${id}`;
        }
        if (category === "itinerary") {
          obj.params.itinerary = `i-${id}`;
        }
        if (category === "subItinerary") {
          obj.params.subItinerary = `si-${id}`;
        }
        if (category === "location") {
          obj.params.location = `l-${id}`;
        }
        if (category === "monument") {
          obj.params.monument = `m-${id}`;
        }
        // Reset Route parameter no matter what (side navigation doesn't keep Route information)
        obj.params.route = null;
      }
      // Push created object to $router to load new page
      this.$router.push(obj);
    },
    // Builds API URL for getting full list of items - reuse for all categories
    // ex. "theme", "themes", "themeId", "themeName"
    getList: function (prop, category, idKey, nameKey) {
      let url;
      if (this.$route.name !== "itineraries") {
        // console.log(category);
        if (category === "filters") {
          // if (this.filter && this.territory) {
          //   url = `${process.env.VUE_APP_API_ENDPOINT}/territories?`;
          // } else {
          url = `${process.env.VUE_APP_API_ENDPOINT}/countries/${this.strip(this.country)}?`;
          // }
        } else {
          url = `${process.env.VUE_APP_API_ENDPOINT}/${category}?`;
        }
      } else if (this.$route.name === "itineraries") {
        // URLs for Itineraries/Sub-Itineraries are different structure
        if (category === "subItineraries") {
          url = `${process.env.VUE_APP_API_ENDPOINT}/itineraries/sub-itineraries?`;
        } else {
          url = `${process.env.VUE_APP_API_ENDPOINT}/itineraries/${category}?`;
        }
      }

      if (this.theme) {
        url += `&t=${this.strip(this.theme)}`;
      }
      if (this.country) {
        // Do not apply Country filter when full list of Countries is required
        if (!(this.$route.name !== "themes" && category === "countries") && category !== "filters") {
          url += `&c=${this.strip(this.country)}`;
        }
      }
      if (this.filter) {
        // Do not apply Filter filter when category is Filter (need full list of Filters)
        if (category !== "filters" || category !== "territories") {
          url += `&f=${this.strip(this.filter)}`;
        }
      }
      if (this.territory) {
        // Do not apply Territory filter when category is Territory (need full list of Territories)
        if (category !== "territories") {
          url += `&tr=${this.strip(this.territory)}`;
        }
      }
      if (this.itinerary) {
        url += `&i=${this.strip(this.itinerary)}`;
      }
      if (this.location && category === "monuments") {
        url += `&l=${this.strip(this.location)}`;
      }
      // console.log(url);

      // Push names for populating dropdowns
      let push = (data, array) => {
        for (let i = 0; i < data.length; i++) {
          let nameArray = data[i][nameKey];
          let name;
          for (let j = 0; j < nameArray.length; j++) {
            if (nameArray[j].langId === "en") {
              // If Sub-Itinerary, add Roman numeral before the title
              if (category === "subItineraries") {
                name = `${data[i].subItineraryOrderRoman}. ${nameArray[j].value}`;
              } else {
                name = nameArray[j].value;
              }
              break;
            }
          }
          // If Sub-Itineraries, need to be able to sort by order
          // Add Sub-Itinerary link in order to get Location/Monument data
          if (category === "subItineraries") {
            array.push({
              [idKey]: data[i][idKey],
              [nameKey]: name,
              order: data[i].subItineraryOrder,
              link: data[i].links.subItinerary,
            });

            // Save current Sub-Itinerary's Location information
            if (data[i].subItineraryId == this.strip(this.subItinerary)) {
              axios({ method: "GET", url: data[i].links.subItinerary }).then(
                (result) => {
                  let locationResult = result.data.locations;
                  this.locationInfo = locationResult;
                  let locations = [];
                  for (let j = 0; j < locationResult.length; j++) {
                    let location = {};
                    location.locationId = locationResult[j].locationId;
                    location.locationName = this.returnValueInLanguage(
                      "en",
                      locationResult[j].locationName
                    ).value;
                    locations.push(location);
                  }
                  this.locations = locations;
                  if (!this.monument) {
                    if (this.subItineraryLocationId) {
                      this.locationId = this.subItineraryLocationId;
                    } else {
                      this.locationId = locations[0].locationId;
                    }
                  } else {
                    this.locationId = this.subItineraryLocation;
                  }
                  // Save Monuments for this Location
                  let monuments = [];
                  for (let j = 0; j < locationResult.length; j++) {
                    if (locationResult[j].locationId == this.locationId) {
                      let locationMonuments = locationResult[j].monuments;
                      for (let k = 0; k < locationMonuments.length; k++) {
                        let monument = {};
                        monument.monumentId = locationMonuments[k].monumentId;
                        monument.monumentName = this.returnValueInLanguage(
                          "en",
                          locationMonuments[k].monumentName
                        ).value;
                        monuments.push(monument);
                      }
                    }
                  }
                  this.monuments = monuments;
                },
                (error) => {
                  console.error(error);
                }
              );
            }
          } else {
            array.push({
              [idKey]: data[i][idKey],
              [nameKey]: name,
            });
          }
        }
      };

      // Get partnership logos for displaying at the bottom of side navigation
      if (this.theme && category === "themes") {
        this.getPartners("themes", this.theme);
      }
      if (this.country && category === "countries") {
        this.getPartners("countries", this.country);
      }
      if (this.territory && category === "territories") {
        this.getPartners("territories", this.territory);
      }
      if (this.itinerary && category === "itineraries") {
        this.getPartners("itineraries", this.itinerary);
      }
      if (this.location && category === "locations") {
        this.getPartners("locations", this.location);
      }

      // Get data, create simple array, and set matching data for display use
      return axios({ method: "GET", url: url }).then(
        (result) => {
          // Monuments are handled differently due to pagination
          if (category !== "monuments") {
            // Check for existence of Territories
            if (category === "territories") {
              if (result.data.length === 0) {
                this.territoriesExist = false;
                return;
              }
            }
            if (category === "filters") {
              if (result.data.availableFilters.length === 0) {
                this.filtersExist = false;
              } else if (result.data.availableFilters.length > 0) {
                this.filtersExist = true;
              }
            }
            // Create and populate array with ID and Name
            let array = [];
            if (category !== "filters") {
              push(result.data, array);
            } else {
              let availableFilters = result.data.availableFilters;
              for (let i = 0; i < availableFilters.length; i++) {
                array.push({
                  [idKey]: availableFilters[i].id,
                  [nameKey]: availableFilters[i].description,
                });
              }
            }
            // console.log(array);
            // Sort Sub-Itineraries by order
            if (category === "subItineraries") {
              array.sort(function (a, b) {
                let orderA = a.order;
                let orderB = b.order;
                return orderA < orderB ? -1 : orderA > orderB ? 1 : 0;
              });
              // Sort all others alphabetically
            } else {
              this.nameSort(array, nameKey);
            }
            this[category] = array;
          } else {
            let array = [];
            push(result.data.data, array);
            // Save number of pages of results
            let pages = result.data.meta["last_page"];
            // Only run if there is more than one page of results
            if (pages > 1) {
              // Strip page number off base URL
              let base = result.data.links.first;
              base = base.substring(0, base.length - 1);
              // Create requests array (for Promise.all)
              let requests = [];
              // Start at 2 (page 1 already returned)
              for (let i = 2; i <= pages; i++) {
                requests.push(axios.get(`${base}${i}`));
              }
              // Get all pages of results and push Monuments into array
              Promise.all(requests).then((results) => {
                let returned = results.map((r) => r.data);
                for (let i = 0; i < returned.length; i++) {
                  push(returned[i].data, array);
                }
                this.nameSort(array, nameKey);
                this[category] = array;
              });
            } else {
              this.nameSort(array, nameKey);
              this[category] = array;
            }
          }
        },
        (error) => {
          console.error(error);
        }
      );
    },
    getPartners: function (category, filter) {
      // Use params, but "=" needed for URLs
      let formatted = filter.replace("-", "=");
      let url = `${process.env.VUE_APP_API_ENDPOINT}/featured-partnership?${formatted}`;
      return axios({ method: "GET", url: url }).then(
        (result) => {
          let results = result.data;
          let partners = [];
          // Save relevant partner data
          for (let i = 0; i < results.length; i++) {
            let details = {};
            details.title = this.returnValueInLanguage(
              "en",
              results[i].details
            ).title;
            details.logo = results[i].logo;
            details.url = this.returnValueInLanguage(
              "en",
              results[i].details
            ).url;
            partners.push(details);
          }
          this.partners[category] = partners;
          this.combinePartners();
        },
        (error) => {
          console.error(error);
        }
      );
    },
    // Remove duplicates of logos for display
    combinePartners: function () {
      let array = [];
      for (let type in this.partners) {
        if (this.partners[type].length > 0) {
          for (let i = 0; i < this.partners[type].length; i++) {
            array.push(this.partners[type][i]);
          }
        }
      }
      // https://stackoverflow.com/a/53543030
      const keys = ["title"],
        filtered = array.filter(
          (
            (s) => (o) =>
              ((k) => !s.has(k) && s.add(k))(keys.map((k) => o[k]).join("|"))
          )(new Set())
        );
      this.allPartners = filtered;
    },
    // Strips the extra identification from URL parameters, leaving just the bare ID
    strip: function (str) {
      if (!str) {
        return "";
      }
      if (str.includes("tr-") || str.includes("si-")) {
        return str.substring(3);
      } else {
        return str.substring(2);
      }
    },
    // Emit used for breadcrumbs (take text from selection option)
    sideLoadEmit: function () {
      let obj = {
        theme: null,
        country: null,
        filter: null,
        territory: null,
        itinerary: null,
        subItinerary: null,
        location: null,
        monument: null,
      };
      this.$nextTick(() => {
        let t = this.$refs.themeSelect;
        if (t && t.options && t.options[t.selectedIndex]) {
          if (this.theme) {
            obj.theme = t.options[t.selectedIndex].text;
          }
        }
        let c = this.$refs.countrySelect;
        if (c) {
          if (c.options[c.selectedIndex]) {
            if (c.options[c.selectedIndex].value !== "") {
              obj.country = c.options[c.selectedIndex].text;
            }
          }
        }
        let f = this.$refs.filterSelect;
        if (f) {
          if (f.options[f.selectedIndex]) {
            if (
              this.filtersExist &&
              f.options[f.selectedIndex].value !== ""
            ) {
              obj.filter = f.options[f.selectedIndex].text;
            }
          }
        }
        let tr = this.$refs.territorySelect;
        if (tr) {
          if (tr.options[tr.selectedIndex]) {
            if (
              this.territoriesExist &&
              tr.options[tr.selectedIndex].value !== ""
            ) {
              obj.territory = tr.options[tr.selectedIndex].text;
            }
          }
        }
        if (this.$route.name === "itineraries") {
          let i = this.$refs.itinerarySelect;
          if (i) {
            if (i.options[i.selectedIndex]) {
              if (i.options[i.selectedIndex].value !== "") {
                obj.itinerary = i.options[i.selectedIndex].text;
              }
            }
          }
          let si = this.$refs.subItinerarySelect;
          if (si) {
            if (si.options[si.selectedIndex]) {
              if (si.options[si.selectedIndex].value !== "") {
                obj.subItinerary = si.options[si.selectedIndex].text;
              }
            }
          }
        }
        let l = this.$refs.locationSelect;
        if (l) {
          if (l.options[l.selectedIndex]) {
            if (l.options[l.selectedIndex].value !== "") {
              obj.location = l.options[l.selectedIndex].text;
            }
          }
        }
        let m = this.$refs.monumentSelect;
        if (m) {
          if (m.options[m.selectedIndex]) {
            if (m.options[m.selectedIndex].value !== "") {
              obj.monument = m.options[m.selectedIndex].text;
            }
          }
        }
        // console.log(obj);
        this.$emit("side-load", obj);
      });
    },
  },
  mounted() {
    this.checkPath(this.$route.name);
    if (this.theme) {
      this.getList(this.theme, "themes", "themeId", "themeName");
    }
    this.getList(this.country, "countries", "countryId", "countryName");
    this.getList(this.filter, "filters", "filterId", "filterName");
    this.getList(this.territory, "territories", "territoryId", "territoryName");
    if (this.$route.name === "itineraries") {
      this.getList(
        this.itinerary,
        "itineraries",
        "itineraryId",
        "itineraryName"
      );
      this.getList(
        this.subItinerary,
        "subItineraries",
        "subItineraryId",
        "subItineraryName"
      );
    }
    if (this.$route.name !== "itineraries") {
      // Only execute if Country has been selected
      if (this.country) {
        this.getList(this.location, "locations", "locationId", "locationName");
        this.getList(this.location, "monuments", "monumentId", "monumentName");
      }
    }
    this.setDisabled();
  },
  updated() {
    this.sideLoadEmit();
  },
};
</script>

<style scoped lang="scss">
#side-navigation {
  position: sticky;
  top: 30px;
  width: 100%;
  border: 1px solid $light-grey;
  box-shadow: 0px 2px 8px $light-grey;
}

#side-navigation-title {
  @include font-size-4;
  color: $dark-green;
  border-bottom: 1px solid $dark-green;
}
#side-navigation-title div {
  padding: 10px 20px;
}
.side-navigation-arrow {
  font-size: 75%;
}
.side-navigation-select {
  position: relative;
  border-bottom: 1px dotted $medium-light-grey;
}
.side-navigation-select:first-child {
  border-bottom: none;
}
.side-navigation-select:last-child {
  border-bottom: none;
  padding-bottom: 0;
  margin-bottom: 10px;
}
.side-navigation-select div {
  padding: 10px 20px 20px 20px;
}
.disabled-overlay {
  height: 100%;
  width: 100%;
  position: absolute;
  top: 0;
  left: 0;
}
.disabled {
  box-shadow: inset 0 0 0 200px rgba($white, 0.4);
}
.select-label {
  @include font-size-5;
  display: inline-block;
  color: $dark-grey;
  margin-bottom: 10px;
}
.select {
  @include font-size-6;
  width: 100%;
  background-color: #fbfbfb;
  border-radius: 5px;
  box-shadow: inset 0px 1px 4px rgba(0, 0, 0, 0.25);
  padding: 4px 0 4px 10px;
}
.bold {
  font-weight: bold;
}
#side-navigation-home {
  color: $dark-green;
}

#filter-reset {
  display: block;
  font-size: 75%;
  color: $dark-green;
  margin-top: 10px;
}

.partners {
  padding: 10px 20px 30px 20px;
}
.partner {
  @include flex-center;
  @include font-size-7;
  flex-direction: column;
}
.partner-logo-container {
  height: 125px;
  width: 85px;
}
.partner-logo {
  @include image-scale;
  object-fit: contain;
  object-position: bottom;
}
.partner-title {
  text-align: center;
  margin-top: 5px;
}
@media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {
  .partner {
    align-self: flex-end;
    margin-bottom: 10px;
  }
  .partner-logo-container {
    height: auto;
  }
}

@media only screen and (max-width: 1199px) {
  #side-navigation-title {
    border: 1px solid $dark-green;
  }
  .partners {
    @include flex-center;
    flex-wrap: wrap;
  }
  .partner {
    margin: 0 20px;
  }
}

@media only screen and (max-width: 949px) {
  #side-navigation-title {
    @include font-size-5;
  }
  .select-label {
    @include font-size-6;
  }
}

@media only screen and (max-width: 499px) {
  #side-navigation-title {
    @include font-size-6;
  }
}
</style>
